import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ActivityResponseRequest,
  ActivityStatus,
  UserActivityRow,
} from '../../shared/models/admin/activity.model';
import { IPagedResponse } from '../../shared/models/IPageResponse';
import { Pageable } from '../models/pageable';
import { SKIP_LOADER } from '../interceptors/loading-context';

@Injectable({ providedIn: 'root' })
export class UserActivityService {
  private readonly activitiesUrl = `${environment.apiResourceUri}/activities/user`;

  constructor(private readonly http: HttpClient) {}

  /**
   * One row per activity for this territory, carrying only this territory's state.
   *
   * Only activities whose window is open today are returned — future ones are not shown yet, and a row
   * disappears the day after `endDate` whether or not it was submitted.
   *
   * @param title case-insensitive contains-match on the activity title; omitted when blank.
   * @param status filters on this territory's own status, not the admin roll-up.
   * @param skipLoader true for background polling, so a badge refresh does not throw the global
   *                   loading overlay over whatever the user is currently doing.
   */
  getActivitiesByTerritory(
    territoryNumber: number,
    pageable: Pageable,
    title?: string,
    status?: ActivityStatus,
    skipLoader = false,
  ): Observable<IPagedResponse<UserActivityRow>> {
    let params = pageable.createPageableParams(new HttpParams());
    const trimmedTitle = title?.trim();
    if (trimmedTitle) {
      params = params.set('title', trimmedTitle);
    }
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<IPagedResponse<UserActivityRow>>(
      `${this.activitiesUrl}/territory/${encodeURIComponent(territoryNumber)}`,
      {
        headers: this.jsonHeaders,
        params,
        ...(skipLoader ? { context: new HttpContext().set(SKIP_LOADER, true) } : {}),
      },
    );
  }

  /** This territory's response on its own — for deep links, since the list already carries the same data. */
  getResponse(activityId: string, territoryNumber: number): Observable<UserActivityRow> {
    return this.http.get<UserActivityRow>(
      this.responseUrl(activityId),
      { headers: this.jsonHeaders, params: new HttpParams().set('territoryNumber', territoryNumber) },
    );
  }

  /**
   * Saves a draft and returns the updated row. Idempotent, so it is safe to call repeatedly.
   *
   * A draft may hold zero photos even when the activity requires one — that rule is enforced on submit.
   * Saving over an already-submitted response reopens it: `submittedDate` is cleared and the territory
   * drops back to `IN_PROGRESS`, which admins will see.
   *
   * `retailerId` must be an enabled retailer in the same territory, or the call is a 400/404.
   */
  saveResponse(activityId: string, request: ActivityResponseRequest): Observable<UserActivityRow> {
    return this.http.put<UserActivityRow>(this.responseUrl(activityId), request, { headers: this.jsonHeaders });
  }

  /**
   * Submits the response and returns the row as `COMPLETE`.
   *
   * Omitting `photoAttachmentIds` submits whatever the draft already holds. Re-submitting is allowed and
   * just moves `submittedDate` forward. A territory's response is shared by everyone in that territory.
   */
  submitResponse(activityId: string, request: ActivityResponseRequest): Observable<UserActivityRow> {
    return this.http.post<UserActivityRow>(
      `${this.responseUrl(activityId)}/submit`,
      request,
      { headers: this.jsonHeaders },
    );
  }

  private responseUrl(activityId: string): string {
    return `${this.activitiesUrl}/${encodeURIComponent(activityId)}/response`;
  }

  private get jsonHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }
}
